// activeOrders (v5.13) — the ONE place that knows how a customer's in-flight
// orders are cached in the browser.
//
// The /order page WRITES these entries and the storefront READS them to show the
// persistent "Order #1042 — being prepared" chip. They previously derived the
// storage key from two independent tenant ladders (the order page from ?tenant=
// only, the storefront from getSubdomain() with a 'staging' dev fallback), so on
// any URL where those disagreed the writer and reader used different keys and
// the chip silently never appeared. Both sides now share this module, so the key
// cannot drift again.
//
// Entries are ephemeral UI state (24h), never a source of truth: every render
// re-verifies against the public /api/dine-in/orders/track endpoint.

const TTL_MS = 24 * 60 * 60 * 1000;

/** The tenant slug used for per-tenant browser storage. Same ladder everywhere:
 *  explicit value → ?tenant=/?subdomain= → hostname subdomain → customer
 *  session → 'main'. Never the API's 'staging' dev default — that would bucket
 *  unrelated local tenants together. */
export function resolveTenantSlug(explicit) {
  const norm = (v) => (v || '').toString().trim().toLowerCase();
  if (norm(explicit)) return norm(explicit);
  try {
    const qp = new URLSearchParams(window.location.search);
    const q = norm(qp.get('tenant') || qp.get('subdomain'));
    if (q) return q;
    const host = window.location.hostname || '';
    if (host.includes('.plateos.io')) return norm(host.replace('.plateos.io', ''));
    const s = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    const sub = norm(s?.tenant_subdomain || s?.tenant?.subdomain);
    if (sub) return sub;
  } catch { /* private mode / bad JSON — fall through */ }
  return 'main';
}

export function activeOrdersKey(tenant) {
  return `plateos_active_orders_${resolveTenantSlug(tenant)}`;
}

/** In-flight orders for this tenant, stale entries dropped. */
export function loadActiveOrders(tenant) {
  try {
    const raw = localStorage.getItem(activeOrdersKey(tenant));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter((o) => o && o.token && Date.now() - (o.created_at || 0) < TTL_MS)
      : [];
  } catch { return []; }
}

export function saveActiveOrders(tenant, list) {
  try {
    localStorage.setItem(activeOrdersKey(tenant), JSON.stringify((list || []).slice(0, 5)));
  } catch { /* private mode — the chip is a convenience, never required */ }
}

/** Remember an order (newest first, de-duped by id). Returns the new list. */
export function rememberActiveOrder(tenant, entry) {
  if (!entry || !entry.token) return loadActiveOrders(tenant);
  const next = [entry, ...loadActiveOrders(tenant).filter((o) => o.id !== entry.id)];
  saveActiveOrders(tenant, next);
  return next;
}

/** Forget an order once it is terminal (picked up / cancelled). */
export function dropActiveOrder(tenant, orderId) {
  const next = loadActiveOrders(tenant).filter((o) => o.id !== orderId);
  saveActiveOrders(tenant, next);
  return next;
}
